/**
 * RedeemPointsDialog — apply reward points to a service request's fee.
 *
 * Flow (single modal):
 *   1. Pick an eligible service request (one with a fee, not yet completed).
 *   2. Enter points to redeem — Max / 50% quick chips + a number field.
 *   3. See a live "you save ₹X, you pay ₹Y" preview.
 *   4. Confirm → POST /rewards/redeem → success summary.
 *
 * Client-side caps (≤ wallet balance, ≤ 50% of the fee) exist purely for UX —
 * the backend enforces the real limits and its rejection message is surfaced
 * verbatim. Built without Radix (Dialog is intentionally not enabled in
 * @finmin/ui) — a lightweight, focus-managed modal instead.
 */
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowRight,
  CheckCircle2,
  Coins,
  Info,
  Wallet,
  X,
} from 'lucide-react';
import {
  Button,
  ErrorMessage,
  FancySelect,
  Input,
  Spinner,
  type FancySelectOption,
  useToast,
} from '@finmin/ui';
import { formatCurrency } from '@finmin/utils';
import {
  useMyServiceRequests,
  type CustomerServiceRequestResponse,
} from '@/api/service-requests';
import { useRedeemPoints, type RewardRedeemResponse } from '@/api/rewards';
import { getServiceMeta } from '@/features/dashboard/constants';
import { DEFAULT_MAX_REDEMPTION_PCT, formatPoints, pointsToInr } from '../utils';

const PALETTE_FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const PALETTE_MINT   = 'hsl(var(--palette-mint,   154  38% 94%))';

// ── Form schema ─────────────────────────────────────────────────────────────
// Static shape only — the dynamic per-service cap is enforced via clamping +
// the submit guard, with the backend as the final authority.
const schema = z.object({
  serviceRequestId: z.string().min(1, 'Choose a service to apply points to'),
  points: z
    .number({ invalid_type_error: 'Enter how many points to redeem' })
    .int('Use whole points')
    .positive('Enter how many points to redeem'),
});
type RedeemFormValues = z.infer<typeof schema>;

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Fee we anchor the 50% cap on — lower bound when a range is quoted. */
function feeBasis(sr: CustomerServiceRequestResponse): number {
  return sr.customer_service_fee_min ?? sr.customer_service_fee_max ?? 0;
}

function feeDisplay(sr: CustomerServiceRequestResponse): string {
  const min = sr.customer_service_fee_min;
  const max = sr.customer_service_fee_max;
  if (min != null && max != null && max !== min) {
    return `${formatCurrency(min)} – ${formatCurrency(max)}`;
  }
  return formatCurrency(min ?? max ?? 0);
}

function serviceTitle(sr: CustomerServiceRequestResponse): string {
  return sr.custom_service_name?.trim() || getServiceMeta(sr.service_type).title;
}

/** Redeemable against a service: it has a positive fee and isn't finished. */
function isEligible(sr: CustomerServiceRequestResponse): boolean {
  return feeBasis(sr) > 0 && sr.customer_workflow_state !== 'service_request_complete';
}

// ── Component ─────────────────────────────────────────────────────────────────

interface RedeemPointsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Current wallet balance — used for the balance cap + summary. */
  availableBalance: number;
  /** Optionally lock the dialog to a single service request. */
  presetServiceRequestId?: string;
}

export function RedeemPointsDialog({
  open,
  onClose,
  availableBalance,
  presetServiceRequestId,
}: RedeemPointsDialogProps) {
  const { toast } = useToast();
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [result, setResult] = React.useState<RewardRedeemResponse | null>(null);

  const srQuery = useMyServiceRequests(undefined, { enabled: open });
  const eligible = React.useMemo(
    () => (srQuery.data ?? []).filter(isEligible),
    [srQuery.data],
  );

  const redeem = useRedeemPoints({
    onSuccess: (data) => {
      setResult(data);
      toast.success(
        'Points redeemed',
        `You saved ${formatCurrency(data.discount_amount)} on this service.`,
      );
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<RedeemFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { serviceRequestId: presetServiceRequestId ?? '', points: 0 },
  });

  const selectedId = watch('serviceRequestId');
  const points = watch('points');
  const selectedSr = eligible.find((sr) => sr.id === selectedId) ?? null;

  const fee = selectedSr ? feeBasis(selectedSr) : 0;
  const capByFee = Math.floor(fee * DEFAULT_MAX_REDEMPTION_PCT);
  const maxRedeemable = Math.max(0, Math.min(availableBalance, capByFee));

  const discount = pointsToInr(Math.min(points || 0, maxRedeemable));
  const payable = Math.max(0, fee - discount);
  const canSubmit =
    !!selectedSr && points >= 1 && points <= maxRedeemable && !redeem.isPending;

  // ── Lifecycle: reset on open, clamp points to the per-service cap ──────────
  React.useEffect(() => {
    if (open) {
      setResult(null);
      redeem.reset();
      reset({ serviceRequestId: presetServiceRequestId ?? '', points: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetServiceRequestId]);

  // Auto-select when there's exactly one eligible service and nothing chosen.
  React.useEffect(() => {
    if (open && !selectedId && eligible.length === 1) {
      setValue('serviceRequestId', eligible[0]!.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eligible, selectedId]);

  // Keep the entered points within the cap as the selection changes.
  React.useEffect(() => {
    if (points > maxRedeemable) setValue('points', maxRedeemable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxRedeemable]);

  // ── Modal behaviours: escape to close + lock background scroll ─────────────
  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !redeem.isPending) onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, redeem.isPending]);

  if (!open) return null;

  const srOptions: FancySelectOption[] = eligible.map((sr) => ({
    value: sr.id,
    label: serviceTitle(sr),
    hint: feeDisplay(sr),
  }));

  function onSubmit(values: RedeemFormValues) {
    if (!selectedSr || values.points > maxRedeemable) return;
    redeem.mutate({ service_request_id: values.serviceRequestId, points: values.points });
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="presentation"
      onMouseDown={() => {
        if (!redeem.isPending) onClose();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm animate-fade-in" aria-hidden />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="redeem-title"
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-lg bg-background rounded-t-2xl sm:rounded-2xl shadow-xl border border-border focus:outline-none animate-slide-up-fade max-h-[92vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            <span
              className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: PALETTE_MINT }}
              aria-hidden
            >
              <Coins className="h-5 w-5" style={{ color: PALETTE_FOREST }} />
            </span>
            <div className="min-w-0">
              <h2 id="redeem-title" className="font-display font-medium text-lg text-foreground tracking-tight">
                Redeem reward points
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Balance{' '}
                <span className="font-semibold text-foreground">
                  {formatPoints(availableBalance)} pts
                </span>{' '}
                · 1 pt = ₹1 off
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !redeem.isPending && onClose()}
            className="shrink-0 h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        {result ? (
          <RedeemSuccess result={result} onDone={onClose} />
        ) : eligible.length === 0 ? (
          <NoEligibleServices loading={srQuery.isLoading} error={srQuery.error?.message ?? null} />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-5">
            {/* 1. Service picker */}
            <div className="space-y-1.5">
              <label htmlFor="redeem-sr" className="text-sm font-medium text-foreground">
                Apply points to
              </label>
              <Controller
                control={control}
                name="serviceRequestId"
                render={({ field }) => (
                  <FancySelect
                    id="redeem-sr"
                    options={srOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Choose a service request"
                    invalid={!!errors.serviceRequestId}
                    leadingIcon={Wallet}
                    ariaLabel="Service request to apply points to"
                  />
                )}
              />
              {errors.serviceRequestId && (
                <p className="text-xs text-destructive">{errors.serviceRequestId.message}</p>
              )}
            </div>

            {/* 2. Points input — only meaningful once a service is chosen */}
            {selectedSr && (
              <div className="space-y-2 animate-slide-down-fade">
                <div className="flex items-center justify-between">
                  <label htmlFor="redeem-points" className="text-sm font-medium text-foreground">
                    Points to redeem
                  </label>
                  <span className="text-xs text-muted-foreground">
                    Max {formatPoints(maxRedeemable)} pts here
                  </span>
                </div>

                <Controller
                  control={control}
                  name="points"
                  render={({ field }) => (
                    <>
                      <Input
                        id="redeem-points"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={maxRedeemable}
                        value={field.value === 0 ? '' : String(field.value)}
                        onChange={(e) => {
                          const raw = Math.floor(Number(e.target.value));
                          if (!Number.isFinite(raw) || raw <= 0) return field.onChange(0);
                          field.onChange(Math.min(raw, maxRedeemable));
                        }}
                        placeholder="0"
                        aria-invalid={!!errors.points}
                      />
                      <div className="flex flex-wrap gap-2">
                        {([
                          { label: '25%', value: Math.floor(maxRedeemable * 0.25) },
                          { label: '50%', value: Math.floor(maxRedeemable * 0.5) },
                          { label: 'Max', value: maxRedeemable },
                        ] as const).map((chip) => (
                          <button
                            key={chip.label}
                            type="button"
                            disabled={chip.value < 1}
                            onClick={() => field.onChange(chip.value)}
                            className="px-3 py-1 rounded-full border border-border text-xs font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {chip.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                />

                {/* Live preview */}
                <div
                  className="rounded-xl p-4 mt-1 space-y-2"
                  style={{ backgroundColor: PALETTE_MINT }}
                >
                  <PreviewRow label="Service fee" value={feeDisplay(selectedSr)} />
                  <PreviewRow label="Reward discount" value={`− ${formatCurrency(discount)}`} accent />
                  <div className="h-px bg-foreground/10" />
                  <PreviewRow
                    label={
                      selectedSr.customer_service_fee_max != null &&
                      selectedSr.customer_service_fee_max !== fee
                        ? 'You pay (est.)'
                        : 'You pay'
                    }
                    value={formatCurrency(payable)}
                    bold
                  />
                </div>
              </div>
            )}

            {/* Server-authoritative error (e.g. cap exceeded, race on balance) */}
            <ErrorMessage message={redeem.error?.message ?? null} />

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={redeem.isPending}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={!canSubmit}>
                {redeem.isPending ? (
                  <>
                    <Spinner className="h-4 w-4" /> Redeeming…
                  </>
                ) : (
                  <>
                    Redeem {points >= 1 ? formatPoints(points) : ''} pts
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function PreviewRow({
  label,
  value,
  accent,
  bold,
}: {
  label: string;
  value: string;
  accent?: boolean;
  bold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={['tabular-nums', bold ? 'font-bold text-base' : 'font-medium'].join(' ')}
        style={{ color: accent || bold ? PALETTE_FOREST : undefined }}
      >
        {value}
      </span>
    </div>
  );
}

function RedeemSuccess({ result, onDone }: { result: RewardRedeemResponse; onDone: () => void }) {
  return (
    <div className="p-6 text-center space-y-4">
      <div
        className="mx-auto h-14 w-14 rounded-full flex items-center justify-center"
        style={{ backgroundColor: PALETTE_MINT }}
      >
        <CheckCircle2 className="h-7 w-7 text-success" aria-hidden />
      </div>
      <div>
        <p className="font-display font-medium text-lg text-foreground">Points redeemed</p>
        <p className="text-sm text-muted-foreground mt-1">
          {formatPoints(result.redeemed_points)} pts applied —{' '}
          <span className="font-semibold text-foreground">
            {formatCurrency(result.discount_amount)} off
          </span>{' '}
          this service.
        </p>
      </div>
      <div className="rounded-xl border border-border p-3 flex items-center justify-center gap-2 text-sm">
        <Wallet className="h-4 w-4 text-muted-foreground" aria-hidden />
        <span className="text-muted-foreground">Remaining balance</span>
        <span className="font-semibold text-foreground tabular-nums">
          {formatPoints(result.remaining_balance)} pts
        </span>
      </div>
      <Button type="button" size="sm" onClick={onDone} className="w-full">
        Done
      </Button>
    </div>
  );
}

function NoEligibleServices({ loading, error }: { loading: boolean; error: string | null }) {
  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }
  return (
    <div className="p-6 text-center space-y-3">
      {error ? (
        <ErrorMessage message={error} />
      ) : (
        <>
          <div
            className="mx-auto h-12 w-12 rounded-full flex items-center justify-center"
            style={{ backgroundColor: PALETTE_MINT }}
          >
            <Info className="h-6 w-6" style={{ color: PALETTE_FOREST }} aria-hidden />
          </div>
          <p className="text-sm font-medium text-foreground">No service to redeem against yet</p>
          <p className="text-sm text-muted-foreground">
            Points apply to a service fee. Start a service request, then come back to
            redeem up to 50% of its fee.
          </p>
          <a
            href="/dashboard"
            className="inline-flex items-center gap-1 text-sm font-semibold"
            style={{ color: PALETTE_FOREST }}
          >
            Browse services
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </a>
        </>
      )}
    </div>
  );
}
